import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, LoadingSpinner } from '@components/common';
import { ModelCardCollection } from '../../ModelCardCollection';
import { useLanguage } from '@contexts/LanguageContext';
import CollectionPagination from './CollectionPagination';

export const CollectionResults = ({
    // Data
    models = [],
    pagination,

    // States
    loading = false,
    error = null,
    hasActiveFilters = false,

    // Display options
    displayType = 'grid',
    displayOptions = {},
    actions = null,

    // Navigation
    onModelClick = null,

    // Pagination props
    enablePagination = true,
    externalModels = null,
    currentPage = 1,
    resourcesPerPage = 24,
    order_by = null,
    pageSizeOptions = [24, 48, 96],

    // Event handlers
    onPageChange,
    onResourcesPerPageChange,
    onRefresh
}) => {
    const { t } = useLanguage();
    const navigate = useNavigate();

    // Default navigation logic based on model type
    const getDefaultNavigationPath = useCallback((model) => {
        switch (model.model_type) {
            case 'resource':
                return `/resources/${model.id}`;
            case 'category':
                return `/categories/${model.id}`;
            case 'location':
                return `/locations/${model.id}`;
            case 'borrowing':
                return `/borrowings/${model.id}`;
            case 'consumption':
                return `/consumptions/${model.id}`;
            case 'refill':
                return `/refills/${model.id}`;
            case 'tag':
                return `/tags/${model.id}`;
            default:
                return null;
        }
    }, []);

    const handleModelClick = useCallback((model) => {
        if (onModelClick) {
            onModelClick(model);
        } else {
            // Navigation automatique par défaut
            const path = getDefaultNavigationPath(model);
            if (path) {
                navigate(path);
            } else {
                // Handle unknown model types
                console.warn('Unknown model type:', model.model_type);
            }

        }
    }, [onModelClick, getDefaultNavigationPath, navigate]);

    // Memoize the getModelActions function to prevent unnecessary re-renders
    const getModelActions = useCallback(() => actions, [actions]);

    // Calculate pagination values
    const totalPages = Math.ceil(pagination.total_items / resourcesPerPage);
    const hasNext = currentPage < totalPages;
    const hasPrevious = currentPage > 1;

    // Render loading state
    if (loading) {
        return (
            <div className="collection-results">
                <div className="loading-container">
                    <LoadingSpinner />
                    <p>{t('collection.loading')}</p>
                </div>
            </div>
        );
    }

    // Render error state
    if (error) {
        return (
            <div className="collection-results">
                <Card className="error-card">
                    <div className="error-content">
                        <h3>{t('collection.errorLoading')}</h3>
                        <p>{error}</p>
                        <button onClick={onRefresh} className="retry-button" type="button">
                            {t('collection.tryAgain')}
                        </button>
                    </div>
                </Card>
            </div>
        );
    }

    // Render empty state
    if (models.length === 0) {
        return (
            <div className="collection-results">
                <Card className="empty-state-card">
                    <div className="empty-state">
                        <h3>{t('collection.noDataFound')}</h3>
                        <p>
                            {hasActiveFilters
                                ? t('collection.tryAdjustingFilters')
                                : null
                            }
                        </p>
                    </div>
                </Card>
            </div>
        );
    }

    // Render results with data
    return (
        <div className="collection-results">

            <div className={`models-${displayType}`}>
                <ModelCardCollection
                    models={models}
                    displayType={displayType}
                    displayOptions={displayOptions}
                    onModelClick={handleModelClick}
                    onRefresh={onRefresh}
                    getModelActions={getModelActions}
                />
            </div>

            <CollectionPagination
                enablePagination={enablePagination}
                externalModels={externalModels}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={onPageChange}
                itemsPerPage={resourcesPerPage}
                onItemsPerPageChange={onResourcesPerPageChange}
                itemsPerPageOptions={pageSizeOptions}
                totalItems={pagination.total_items}
                hasNext={hasNext}
                hasPrevious={hasPrevious}
                order_by={order_by}
            />
        </div>
    );
};

export default CollectionResults;
